import React from "react";
import "./AdditionalSolutions.css";
import { CDN_BASE_URL } from "../../config/cdn";

// ✅ Reusable Section Component (Dynamic Background)
const SolutionsSection = ({ title, items, bgColor }) => (
    <section
        className="solutions-section"
        style={{
            background: bgColor || "var(--primary-bg)",
            transition: "background 0.3s ease",
        }}
    >
        <h2>{title}</h2>

        <div className="solutions-grid">
            {items.map((item, i) => (
                <div className="solution-card" key={i}>

                    {/* 🔹 ICON BOX (shows image if available, otherwise stays grey) */}
                    <div className="icon-box">
                        {item.icon ? (
                            <img src={item.icon} alt={item.title} className="icon-img" />
                        ) : null}
                    </div>

                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                </div>
            ))}
        </div>
    </section>
);


// ✅ Hero Section (with background image)
const SolutionsHero = () => (
    <div
        className="solutions-hero"
        style={{
            backgroundImage: `url("${CDN_BASE_URL}/images/solutionsbg.png")`,

        }}
    >
        <div className="overlay"></div>
        <div className="hero-content">
            <h1 className="gradient-text">Our Products & Services</h1>
            <p>
                Innovative solutions crafted to streamline operations, boost revenue, and elevate patient care.
            </p>
        </div>
    </div>
);

// ✅ Hero Subtext Section (between image and first section)
const HeroSubtext = () => (
    <div className="hero-subtext-section">
        <p>
            <strong>
                Discover a tailored portfolio built to meet your unique objectives. Explore our offerings—organized by <span className="highlight">service specialty</span> & <span className="highlight">customer type</span>—to find the perfect solution for your needs.
            </strong>
        </p>
    </div>
);


// ✅ Contact CTA Section
const ContactCTA = () => (
    <section className="contact-cta">
        <div className="cta-content">
            <h2>Let's talk.</h2>
            <p>Elevate your care strategy. Reach out and explore our tailored solutions.</p>
            <a href="/contactUS">
                <button>Contact Us</button>
            </a>
        </div>
    </section>
);

// ✅ Full Page Component
const AdditionalSolutions = () => {
    // 🎯 Dynamic data for each section
    const serviceSpecialty = [
        {
            title: "Revenue Cycle Management",
            desc: "Optimize your billing and collections with our advanced RCM solutions. We enhance financial performance by reducing claim denials, accelerating reimbursements, and delivering actionable insights to keep your cash flow robust and compliant.",
            icon: `${CDN_BASE_URL}/images/revenue-cycle-management-optimize-your-billing-and.png`
        },
        {
            title: "Coding Technology & Services (HIM)",
            desc: "Ensure accuracy and compliance in clinical documentation with our coding solutions. Our Health Information Management services streamline coding processes, minimize errors, and adapt to evolving regulations.",
            icon: `${CDN_BASE_URL}/images/coding-technology---services--him--ensure-accuracy.jpg`
        },
        {
            title: "Patient Access & Engagement",
            desc: "Transform the patient experience with intuitive access and engagement tools. Simplify scheduling, enhance communication, and empower patients through self-service portals for better outcomes.",
            icon: `${CDN_BASE_URL}/icons/patient-access---engagement-transform-the-patient-.png  `
        },
        {
            title: "Electronic Health Record & Information Systems",
            desc: "Centralize patient data & streamline workflows with our EHR solutions for secure, real-time access to comprehensive records.",
            icon: `${CDN_BASE_URL}/icons/electronic-health-record---information-systems----.jpg`
        },
        {
            title: "Population Health & Data Management",
            desc: "Leverage powerful analytics to drive community health initiatives. Our solutions integrate and analyze data across populations, enabling proactive care strategies and more effective disease management for improved overall health outcomes.",
            icon: `${CDN_BASE_URL}/icons/population-health---data-management---icon-for-thi.jpg`
        },
        {
            title: "Revenue Cycle Services & Outsourcing",
            desc: "Focus on delivering quality care while we manage your revenue cycle end-to-end. Our outsourcing services cover everything from billing to denial management, reducing administrative burdens and operational costs while ensuring faster reimbursements.",
            icon: `${CDN_BASE_URL}/icons/revenue-cycle-services---outsourcing--icon-for-thi.jpg`
        },
        {
            title: "Genetic Testing Laboratories",
            desc: "Precision medicine and genomic diagnostics are transforming healthcare — but behind every advanced test lies a complex billing process that demands equal precision. Our specialized billing services are designed to support NGS-based, molecular pathology, and genetic testing laboratories, ensuring faster reimbursements, fewer denials, and complete payer compliance.",
            icon: `${CDN_BASE_URL}/icons/genetic-testing-laboratories--icon-for-this.jpg`
        },
    ];

    const consumerType = [
        {
            title: "Ambulatory Services & Clinics",
            desc: "Tailored for outpatient settings, our solutions streamline administrative tasks, improve cash flow, and boost coding accuracy. We help clinics and ambulatory centers focus on patient care by minimizing delays and maximizing revenue.",
            icon: `${CDN_BASE_URL}/icons/ambulatory-services---clinics-icon-for-this.jpg`
        },
        {
            title: "Individual Providers & Group Practices",
            desc: "Our tools for providers and clinicians cut through the noise of administrative tasks, enabling you to concentrate on patient care. With streamlined workflows and precise coding support, we ensure robust revenue cycles and compliance, letting you focus on what truly matters.",
            icon: `${CDN_BASE_URL}/icons/individual-providers---group-practices-icon-for-th.jpg`
        },
        {
            title: "Practice Administrators",
            desc: "Designed for administrators, our offerings simplify complex billing and financial processes with seamless integrations and actionable insights. Enhance decision-making, reduce overheads, and drive strategic financial performance with clarity and control.",
            icon: `${CDN_BASE_URL}/icons/practice-administrators-icon-for-this.jpg`
        },
        {
            title: "Hospitals",
            desc: "Our comprehensive suite for hospitals is engineered to optimize revenue cycles, integrate advanced EHR systems, and deliver real-time analytics—empowering you to overcome operational challenges while enhancing patient care.",
            icon: `${CDN_BASE_URL}/icons/hospitalsicon-for-this.jpg`
        },
        {
            title: "Diagnostic & Pathology Laboratories",
            desc: "We partner with genetic and molecular diagnostic laboratories to transform complex, multi-payer revenue cycles into streamlined, compliant, and profitable operations. Our deep domain expertise ensures that your science translates seamlessly into sustainable revenue — so you can focus on advancing patient outcomes, while we handle the precision behind your payments.",
            icon: `${CDN_BASE_URL}/icons/diagnostic---pathology-laboratories-icon-for-this.jpg`
        },
    ];


    const additionalSolutions = [
        {
            title: "Quality Payment Program",
            desc: "Our Quality Payment Program solutions empower healthcare providers to meet performance-based reimbursement criteria. By streamlining data collection and reporting, we help you capture incentive opportunities while ensuring compliance and driving improved patient outcomes.",
            icon: `${CDN_BASE_URL}/icons/quality-payment-program-icon-for-this.jpg`
        },
        {
            title: "Merit-Based Incentive Payment System",
            desc: "Our Merit-Based Incentive Payment System (MIPS) solutions simplify complex regulatory requirements, enabling you to optimize reimbursements. We provide the tools and insights necessary to boost performance metrics and mitigate financial risks in a competitive healthcare landscape.",
            icon: `${CDN_BASE_URL}/icons/merit-based-incentive-payment-system-icon-for-this.jpg`
        },
        {
            title: "Meaningful Use",
            desc: "Designed to maximize the benefits of your EHR investments, our Meaningful Use solutions help you meet federal benchmarks. We enable seamless data exchange and improved clinical workflows, ensuring that your practice not only complies with regulations but also delivers superior patient care.",
            icon: `${CDN_BASE_URL}/icons/meaningful-use-icon-for-this.jpg`
        },
        {
            title: "Chronic Care Management",
            desc: "Our Chronic Care Management services focus on coordinated care strategies that enhance patient outcomes and generate consistent revenue streams. We support your practice in managing long-term care for chronic conditions, reducing hospital readmissions, and improving overall patient well-being.",
            icon: `${CDN_BASE_URL}/icons/chronic-care-management-icon-for-this.jpg`
        },
        {
            title: "Transitional Care Management",
            desc: "We offer comprehensive Transitional Care Management solutions to ensure smooth patient handoffs between care settings. By enhancing care coordination and follow-up services, our approach minimizes readmissions, improves patient satisfaction, and secures optimal reimbursements.",
            icon: `${CDN_BASE_URL}/icons/transitional-care-management-icon-for-this.jpg`
        },
        {
            title: "Data Analytics",
            desc: "Harness the power of data with our robust analytics solutions. We transform complex healthcare data into actionable insights that drive operational improvements, inform strategic decisions, and ultimately enhance both clinical and financial performance.",
            icon: `${CDN_BASE_URL}/icons/data-analytics-icon-for-this.jpg`
        },
        {
            title: "EMR Training",
            desc: "Our EMR Training programs are tailored to meet the needs of your clinical teams. We offer hands-on, role-specific training that ensures smooth transitions during system upgrades or new implementations, boosting efficiency and user confidence.",
            icon: `${CDN_BASE_URL}/icons/emr-training-icon-for-this.jpg`
        },
        {
            title: "Coding Compliance & Audits",
            desc: "Stay ahead of regulatory changes with our Coding Compliance & Audits services. We rigorously assess your coding practices to minimize errors and optimize reimbursement, reducing compliance risks while safeguarding your revenue.",
            icon: `${CDN_BASE_URL}/icons/coding-compliance---audits-icon-for-this.jpg`
        },
    ];


    return (
        <>
            <SolutionsHero />

            {/* 🔹 New text between hero and first section */}
            <HeroSubtext />

            <SolutionsSection
                title="By Service Specialty"
                items={serviceSpecialty}
                bgColor="#efe3e6ff"
            />

            <SolutionsSection
                title="By Consumer Type"
                items={consumerType}
                bgColor="#f0f7f6"
            />

            <SolutionsSection
                title="Additional Solutions"
                items={additionalSolutions}
                bgColor="#e8fdfb"
            />

            <ContactCTA />
        </>
    );

};

export default AdditionalSolutions;
